import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';
import type { RootState } from '@/app/store';

import sessionAPI from './sessionAPI';
import type {
  AuthTokenData,
  CommonServicePayload,
  UserValidation,
} from './sessionAPI';
import appLocalStorage from './appLocalStorage';
import { isTokenExpired } from './utils/helpers';

export const Roles = {
  SUPER: 'super',
  ADMIN: 'admin',
  USER: 'user',
  UNKNOWN: '',
} as const;
export type Roles = (typeof Roles)[keyof typeof Roles];

const rolelist = [
  { role: Roles.SUPER, priority: 1 },
  { role: Roles.ADMIN, priority: 2 },
  { role: Roles.USER, priority: 3 },
];

export const RolesEnv = {
  PROD: 'prod',
  TEST: 'test',
} as const;
export type RolesEnv = (typeof RolesEnv)[keyof typeof RolesEnv];

export interface User {
  email: string;
  givenName: string;
  familyName: string;
}

export interface SessionState {
  user: User | null;
  loading: boolean;
  appInitialised: boolean;
  role: Roles;
  userAccessProfile: UserValidation | null;
  userRejected: boolean;
}

const initialState: SessionState = {
  user: null,
  loading: false,
  appInitialised: false,
  role: Roles.UNKNOWN,
  userAccessProfile: null,
  userRejected: false,
};

export const signIn = createAsyncThunk(
  'session/signIn',
  (_, { rejectWithValue }) => {
    try {
      return sessionAPI.signIn();
    } catch (error) {
      const { message } = error as Error;
      return rejectWithValue(message);
    }
  }
);

export const beginSession = createAsyncThunk<
  User | null,
  string | null,
  { rejectValue: string }
>('session/beginSession', async (code: string | null, { rejectWithValue }) => {
  try {
    const tokenData = appLocalStorage.getTokenData();
    if (tokenData) {
      let storedIdToken: any = jwtDecode(tokenData.id_token);
      if (isTokenExpired(storedIdToken)) {
        const tokenResponse = await sessionAPI.refreshAccessToken(
          tokenData?.refresh_token
        );
        const newTokenData = {
          ...tokenResponse?.data,
          refresh_token: tokenData?.refresh_token,
        };
        storedIdToken = jwtDecode(newTokenData?.id_token);
        await appLocalStorage.setTokenData(newTokenData);
      }
      return {
        email: storedIdToken.email,
        givenName: storedIdToken.given_name,
        familyName: storedIdToken.family_name,
      };
    }
    if (!code) {
      return null;
    }
    const tokenDataResponse: AxiosResponse<AuthTokenData> =
      await sessionAPI.getAuthToken(code);

    appLocalStorage.setTokenData(tokenDataResponse.data);

    const idToken: any = jwtDecode(tokenDataResponse.data.id_token);

    return {
      email: idToken.email,
      givenName: idToken.given_name,
      familyName: idToken.family_name,
    };
  } catch (error) {
    const { message } = error as Error;
    return rejectWithValue(message);
  }
});

export const loadUserProfile = createAsyncThunk<
  UserValidation | null,
  CommonServicePayload,
  { rejectValue: string }
>(
  'tags/loadUserProfile',
  async (commonServicePayload: CommonServicePayload, { rejectWithValue }) => {
    try {
      const userAccessProfile = appLocalStorage.getUserAccessProfile();

      if (userAccessProfile) {
        return userAccessProfile;
      }
      if (commonServicePayload?.token) {
        const response = await sessionAPI.getUserProfile(commonServicePayload);

        appLocalStorage.setAccessUserProfile(response.data);
        return response.data;
      }
      return null;
    } catch (err) {
      const { message } = err as Error;
      return rejectWithValue(message);
    }
  }
);

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setIsAuthenticated: (state, action) => {
      state.appInitialised = true;
    },
    clearUserData: state => {
      state.user = null;
      appLocalStorage.clearLocalStorage();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(beginSession.pending, state => {
        state.loading = true;
      })
      .addCase(beginSession.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(beginSession.rejected, state => {
        state.loading = false;
      })
      .addCase(loadUserProfile.pending, state => {
        state.loading = true;
        state.userRejected = false;
      })
      .addCase(loadUserProfile.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload) {
          const {
            roles: { access },
          } = action.payload;

          const currentRoleEnv: RolesEnv =
            import.meta.env.VITE_REACT_APP_APP_ENV === RolesEnv.PROD
              ? RolesEnv.PROD
              : RolesEnv.TEST;

          const currentAccessRoles = access.find(
            accessRoles => accessRoles.env === currentRoleEnv
          )?.roles;

          const role =
            currentAccessRoles?.length === 1
              ? currentAccessRoles[0].role
              : currentAccessRoles?.sort(
                  (a, b) =>
                    (rolelist.find(e => e.role === a.role)?.priority || 0) -
                    (rolelist.find(e => e.role === b.role)?.priority || 1)
                )[0]?.role;

          state.userRejected = role ? false : true;
          state.role = (role as Roles) || Roles.UNKNOWN;
          state.userAccessProfile = action.payload;
        }
        state.appInitialised = true;
      })
      .addCase(loadUserProfile.rejected, state => {
        state.loading = false;
        state.appInitialised = true;
        state.userRejected = true;
      });
  },
});

export const { clearUserData, setIsAuthenticated } = sessionSlice.actions;

export const selectSessionLoading = (state: RootState): boolean =>
  state.session.loading;
export const selectSessionUser = (state: RootState): User | null =>
  state.session.user;
export const selectIsAppInitialised = (state: RootState): boolean =>
  state.session.appInitialised;
export const selectUserAccessProfile = (
  state: RootState
): UserValidation | null => state.session.userAccessProfile;
export const selectUserRole = (state: RootState): Roles => state.session.role;
export const selectUserRejected = (state: RootState): boolean =>
  state.session.userRejected;

export default sessionSlice.reducer;
